import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Realtime presence for the pair's chat. Tracks online + typing on a
// shared channel keyed to the two user ids (order-independent so both
// sides land on the same topic). Exposes the partner's live state and
// a setTyping() the composer calls; typing auto-clears after a short
// idle so a stuck "typing…" can't linger.
export function useChatPresence(partnerId) {
  const { user } = useAuth()
  const userId = user?.id || null

  const [partnerOnline, setPartnerOnline] = useState(false)
  const [partnerTyping, setPartnerTyping] = useState(false)
  const channelRef = useRef(null)
  const typingTimerRef = useRef(null)
  const typingRef = useRef(false)

  useEffect(() => {
    if (!userId || !partnerId) return undefined
    const topic = `dm-presence-${[userId, partnerId].sort().join('-')}`
    const channel = supabase.channel(topic, { config: { presence: { key: userId } } })
    channelRef.current = channel

    const readPartner = () => {
      const state = channel.presenceState()
      const entries = state[partnerId]
      const meta = Array.isArray(entries) ? entries[entries.length - 1] : null
      setPartnerOnline(!!meta)
      setPartnerTyping(!!meta?.typing)
    }

    channel
      .on('presence', { event: 'sync' }, readPartner)
      .on('presence', { event: 'join' }, readPartner)
      .on('presence', { event: 'leave' }, readPartner)
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') await channel.track({ online: true, typing: false })
      })

    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [userId, partnerId])

  // Broadcast my typing state; auto-clears 2.5s after the last keystroke.
  const setTyping = useCallback((isTyping) => {
    const channel = channelRef.current
    if (!channel) return
    if (isTyping) {
      if (!typingRef.current) {
        typingRef.current = true
        channel.track({ online: true, typing: true })
      }
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      typingTimerRef.current = setTimeout(() => {
        typingRef.current = false
        channel.track({ online: true, typing: false })
      }, 2500)
    } else {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      typingRef.current = false
      channel.track({ online: true, typing: false })
    }
  }, [])

  return { partnerOnline, partnerTyping, setTyping }
}
